import type { ColumnDef } from '@tanstack/react-table';

import { Can } from '@/components/can/Can';
import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { PERMS } from '@/features/auth/permissions/permissionKeys';
import { ModalSize, ModalVariant } from '@/features/configuration-management/location/table/Column';
import { EditIcon, TrashIcon } from '@/icons';
import { formatDate } from '@/lib/dateFormatter';
import { StandardizedApiError } from '@/services/client/types';
import { DeleteMainPageGroupPayload } from '@/services/mainPage/mainPage.mutations';
import { MainPage } from '@/services/mainPage/mainPage.types';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { EditGroupFormContainer } from '../form/EditGroupFormContainer';

export function getColumns(
  t: (key: string) => string,
  onOpen: <T extends string>(config: {
    title: T;
    modalTitle: string | null;
    okText: React.ReactNode;
    component: React.ReactNode;
    cancelText?: string | React.ReactNode;
    size?: ModalSize;
    onCancel?: () => void;
    image?: boolean;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
  openConfirmation: (data: {
    title: string;
    description?: string | undefined;
    cancelLabel: string;
    actionLabel: string;
    content?: React.ReactNode;
    size?: ModalSize;
    variant?: ModalVariant;
    onAction?: () => void | Promise<void>;
    onCancel?: () => void | Promise<void>;
    isSubmitting?: 'submit' | 'cancel' | null;
  }) => void,
  mutateAsync: UseMutateAsyncFunction<
    {
      message: string;
    },
    StandardizedApiError,
    DeleteMainPageGroupPayload,
    unknown
  >,
  locationId: string,
  canGetStoreList: boolean,
): ColumnDef<MainPage>[] {
  return [
    {
      accessorKey: 'sn',
      header: ({ column }) => (
        <DataTableColumnHeader className="whitespace-nowrap" column={column} title={t('common.page.tableColumn.sn')} />
      ),
      cell: ({ row, table }) => <div className="font-medium">{getSerialNumber(table, row.index)}</div>,
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'groupName',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.groupInfo')} />,
      cell: ({ row }) =>
        canGetStoreList ? (
          <Link
            to="/recommendation-management/main-page/$store-list"
            params={{ 'store-list': row.original.groupId }}
            search={{ breadcrumbReturnTo: window.location.href, locationId }}
            className="font-medium text-xs text-link underline break-all cursor-pointer"
          >
            {row.getValue('groupName')}
          </Link>
        ) : (
          <div className="font-medium text-xs text-muted-foreground underline break-all cursor-not-allowed">
            {row.getValue('groupName')}
          </div>
        ),
      enableHiding: false,
    },
    {
      accessorKey: 'totalStores',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.totalStores')} />
      ),
      cell: ({ row }) => <div className="text-caption font-medium text-primary">{row.getValue('totalStores')}</div>,
      enableHiding: false,
    },
    {
      accessorKey: 'createdDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.createdDate')} />
      ),
      cell: ({ row }) => {
        const date = Number(row.getValue<string>('createdDate'));
        return <div className="text-caption font-medium text-primary">{date ? formatDate(date) : '-'}</div>;
      },
      enableHiding: false,
      enableColumnFilter: true,
    },
    {
      accessorKey: 'updatedDate',
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t('common.page.tableColumn.updatedDate')} />
      ),
      cell: ({ row }) => {
        const date = Number(row.getValue('updatedDate'));
        return <div className="text-caption font-medium text-primary">{date ? formatDate(date) : '-'}</div>;
      },
      enableHiding: false,
    },
    {
      accessorKey: 'remark',
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.remarks')} />,
      cell: ({ row }) => (
        <div className="text-caption font-medium text-primary break-all max-w-[285px] truncate">
          {row.getValue('remark') ? row.getValue('remark') : '-'}
        </div>
      ),
      enableHiding: false,
    },
    {
      header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.action')} />,
      id: 'actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-4">
          <Can rule={{ allOf: [PERMS.recommendation.mainPage.group.update] }}>
            <EditIcon
              size={16}
              className="cursor-pointer text-foreground-subtle"
              onClick={() =>
                onOpen({
                  title: 'Add New Group',
                  okText: t('common.page.modal.submit'),
                  size: 'lg',
                  modalTitle: 'common.page.modal.manageGroup.title',
                  component: <EditGroupFormContainer groupId={row.original.groupId} />,
                })
              }
            />
          </Can>
          <Can rule={{ allOf: [PERMS.recommendation.mainPage.group.delete] }}>
            <TrashIcon
              className="size-4 cursor-pointer text-destructive"
              onClick={() => {
                openConfirmation({
                  title: t('common.page.modal.confirmation.title'),
                  description: t('common.page.modal.deleteGroupMessage'),
                  cancelLabel: t('common.page.modal.no'),
                  actionLabel: t('common.page.modal.confirm'),
                  onAction: async () => {
                    await mutateAsync({ groupId: row.original.groupId });
                  },
                  onCancel: () => {},
                  size: 'lg',
                  variant: 'destructive',
                });
              }}
            />
          </Can>
        </div>
      ),
    },
  ];
}
